import { useEffect, useRef } from "react";
import "./image-carousel.scss";

export default function ImageCarousel({
  images = [],
  speed = 0.5,
  className = "",
  spacing = 0.5,
  slideWidth = 200,
  slideHeight = 300,
}) {
  const track1Ref = useRef(null);
  const track2Ref = useRef(null);
  const offset1 = useRef(0);
  const offset2 = useRef(0);
  const rafRef = useRef(null);
  const totalWidthRef = useRef(0);

  useEffect(() => {
    if (!track1Ref.current) return;

    const spacingPx =
      spacing * parseFloat(getComputedStyle(document.documentElement).fontSize);
    const track1 = track1Ref.current;
    const slides = Array.from(track1.children);

    // Calculate total width including spacing and extra spacing after the last slide
    let totalWidth = 0;
    slides.forEach((slide, index) => {
      totalWidth += slide.offsetWidth;
      totalWidth += spacingPx; // spacing after each slide
    });

    totalWidthRef.current = totalWidth;

    // Position track2 immediately after track1
    offset2.current = totalWidth;

    const animate = () => {
      offset1.current -= speed;
      offset2.current -= speed;

      if (offset1.current <= -totalWidthRef.current) {
        offset1.current = offset2.current + totalWidthRef.current;
      }
      if (offset2.current <= -totalWidthRef.current) {
        offset2.current = offset1.current + totalWidthRef.current;
      }

      if (track1Ref.current && track2Ref.current) {
        track1Ref.current.style.transform = `translate3d(${offset1.current}px,0,0)`;
        track2Ref.current.style.transform = `translate3d(${offset2.current}px,0,0)`;
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [images, spacing, speed]);

  const renderTrack = (ref, keyPrefix) => (
    <div className="carousel__track" ref={ref}>
      {images.map((src, i) => {
        const isLast = i === images.length - 1;
        return (
          <div
            className="carousel__slide"
            key={`${keyPrefix}-${i}`}
            style={{
              width: `${slideWidth}px`,
              height: `${slideHeight}px`,
              marginRight: `${spacing}rem`,
              ...(isLast ? { marginRight: `${spacing}rem` } : {}), // extra spacing after last slide
            }}
          >
            <img src={src} alt="" loading="lazy" />
          </div>
        );
      })}
    </div>
  );

  return (
    <div className={`carousel ${className}`}>
      {renderTrack(track1Ref, "track1")}
      {renderTrack(track2Ref, "track2")}
    </div>
  );
}
